import { Form, Input, InputNumber, Select } from 'antd';
import TrashV2 from 'assets/Icons/trashV2';
import Text from 'components/Text';
import colorTheme from 'constants/color';
import { PACKAGE_TYPE } from 'features/settingService/services/constants';
import { ChevronDown } from 'lucide-react';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';

const DISCOUNT_TYPES = [
  { label: '%', value: 'PERCENT' },
  { label: '$', value: 'MONEY' },
];

const packageTypeOptions = [
  {
    value: PACKAGE_TYPE.BRONZE,
    label: 'Bronze',
  },
  {
    value: PACKAGE_TYPE.SILVER,
    label: 'Silver',
  },
  {
    value: PACKAGE_TYPE.GOLD,
    label: 'Gold',
  },
];

interface PackageTierCardProps {
  field: {
    key: number;
    name: number;
    fieldKey?: number;
  };
  remove: (index: number | number[]) => void;
  calculation: {
    packagePrice: number;
    pricePerSession: number;
    savings: number;
  };
}

const PackageTierCard = ({
  field,
  remove,
  calculation,
}: PackageTierCardProps) => {
  return (
    <TierCard key={field.key}>
      <FormRow className='tier-form-row'>
        <Form.Item
          name={[field.name, 'name']}
          label='Tier name'
          rules={[
            {
              required: true,
              message: 'Please enter tier name',
            },
          ]}
        >
          <Input placeholder='Please enter...' />
        </Form.Item>

        <Form.Item
          name={[field.name, 'quantity']}
          label='Quantity'
          rules={[
            {
              required: true,
              message: 'Please enter quantity',
            },
            {
              type: 'number',
              min: 1,
              message: 'Quantity must be at least 1',
            },
          ]}
        >
          <InputNumber
            min={1}
            precision={0}
            style={{ width: '100%' }}
            placeholder='0'
            controls={false}
          />
        </Form.Item>
        <Form.Item label='Discount' required>
          <DiscountWrapper>
            {/* discountValue */}
            <Form.Item
              name={[field.name, 'discountValue']}
              noStyle
              rules={[
                {
                  required: true,
                  message: 'Please enter discount',
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const discountType = getFieldValue([
                      'tiers',
                      field.name,
                      'discountType',
                    ]);

                    if (value === undefined || value === null) {
                      return Promise.resolve();
                    }

                    if (value < 0) {
                      return Promise.reject(
                        new Error('Discount cannot be negative'),
                      );
                    }

                    if (discountType === 'PERCENT' && value > 100) {
                      return Promise.reject(
                        new Error('Discount cannot exceed 100%'),
                      );
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <StyledInputNumber
                min={0}
                // precision={2}
                placeholder='0'
                controls={false}
              />
            </Form.Item>

            {/* discountType */}
            <Form.Item name={[field.name, 'discountType']} noStyle>
              <StyledSelect
                options={DISCOUNT_TYPES}
                suffixIcon={<ChevronDown />}
              />
            </Form.Item>
          </DiscountWrapper>
        </Form.Item>
        <Form.Item
          name={[field.name, 'type']}
          label='Type'
        >
          <Select suffixIcon={<ChevronDown />} options={packageTypeOptions} popupClassName="reward-select-dropdown"/>
        </Form.Item>

        <CalculationGrid>
          <CalculationItem>
            <Text variant='H8' style={{ lineHeight: '24px' }}>
              Package price
            </Text>

            <CalculationValue>
              <Text variant='H7' color='v2_green_7'>
                {formatCurrency(calculation.packagePrice)}
              </Text>
            </CalculationValue>
          </CalculationItem>

          <CalculationItem>
            <Text variant='H8' style={{ lineHeight: '24px' }}>
              Price / Session
            </Text>

            <CalculationValue>
              <Text variant='H7' color='v2_green_7'>
                {formatCurrency(calculation.pricePerSession)}
              </Text>
            </CalculationValue>
          </CalculationItem>

          <CalculationItem>
            <Text variant='H8' style={{ lineHeight: '24px' }}>
              Savings
            </Text>

            <CalculationValue>
              <Text variant='H7' color='v2_green_7'>
                Save {formatCurrency(calculation.savings)}
              </Text>
            </CalculationValue>
          </CalculationItem>
        </CalculationGrid>
        <button type='button' onClick={() => remove(field.name)}>
          <TrashV2 width={32} height={32} fill={colorTheme.v2_primary_bg_6} />
        </button>
      </FormRow>
    </TierCard>
  );
};

export default PackageTierCard;

const TierCard = styled.div`
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafbfc;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 3fr 0.3fr;
  gap: 16px;

  .ant-input {
    font-family: Poppins;
    font-size: 20px;
    font-weight: 500;
    height: 3.5rem;
    border-color: ${colorTheme.v2_neutral_5};
  }

  .ant-input-number-input {
    font-family: Poppins;
    font-size: 20px;
    font-weight: 500;
    height: 3.5rem;
    border-color: ${colorTheme.v2_neutral_5};
  }

  .ant-form-item-label > label {
    font-weight: 400;
    font-size: 18px;
  }
  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-select-selection-item {
    font-family: Poppins;
    font-size: 24px;
    font-weight: 500;
  }
`;

const CalculationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
`;

const CalculationItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CalculationValue = styled.div`
  height: 56px;
  border: 1px solid ${colorTheme.v2_green_4};
  background: ${colorTheme.v2_green_1};
  border-radius: 4px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  padding: 0 16px;
`;

const DiscountWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 3.5rem;

  .ant-input-number {
    border-radius: 4px 0 0 4px;
    border-color: ${colorTheme.v2_neutral_5};
  }

  .ant-select {
    .ant-select-selector {
      border-radius: 0 4px 4px 0 !important;
    }
  }
`;

const StyledInputNumber = styled(InputNumber)`
  flex: 1;
  width: 0;
  height: 3.5rem;
  border-radius: 4px 0 0 4px;

  .ant-input-number-input {
    height: 3.5rem;
    padding: 0 16px;
    font-size: 20px;
    font-weight: 500;
    font-family: Poppins;
  }
`;

const StyledSelect = styled(Select)`
  width: 80px !important;
  height: 100%;

  .ant-select-selector {
    height: 100% !important;
    display: flex;
    align-items: center;
    padding: 0 16px !important;
    background: ${colorTheme.v2_neutral_3} !important;
    border: 1px solid ${colorTheme.v2_neutral_5} !important;
    border-left: none !important;
  }

  .ant-select-selection-item {
    font-size: 24px;
    font-weight: 600;
    color: #1d2129;
  }

  .ant-select-arrow {
    color: #64748b;
  }
`;
